import { Dispatch, SetStateAction, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { WppActionButton, WppButton, WppIconDone, WppPill, WppTypography } from '@wppopen/components-library-react'
import { GenerateContentModal } from 'components/Modals/GenerateContentModal/GenerateContentModal'
import MenuListPopUp, { MenuItems } from 'components/MenuListPopUp/MenuListPopUp'
import Table from 'components/table'
import useApi from 'hooks/useApi'
import { AddAndEditProjectRegion, GenericModal, ManualUpload, UploadProjectData } from 'components/Modals'
import { FileItemType, uuidv4 } from '@wppopen/components-library'
import TableMetaDataRow from 'components/table/TableMetaDataRow'
import DownloadAllButton from 'components/DownloadAllButton/DownloadAllButton'
import appContext from 'context/appContext'
import { createCsApiUrlFromEndpoint, downloadCSVFile, getType, uploadFileToBucket } from 'utils'
import { columnStructureDraftRegion, columnStructureGeneratedRegion } from './utils'
import { DEFAULT_API_ITEM_LIMITS } from 'utils/constants'
import {
  getPaginatedTableReducerEmptyState,
  paginatedTableReducer,
  setRowData,
  setTaskPosition,
  setTotalItemCount,
} from 'reducers/PaginatedTableReducer/PaginatedTableReducer'
import {
  getActiveRegionRetailers,
  handleLoadMore,
  handleSortingUpdate,
  refreshHandler,
} from '../../pages/ProjectView/utils'
import useCurrentUser from 'hooks/useCurrentUser'
import type {
  ProductsListData,
  ProjectsDetailResult,
  ProjectsRegionsCreatePayload,
  ProjectsRegionsDeleteData,
  ProjectsRegionsPartialUpdateData,
  UploadsCreateData,
  ImportsCreatePayload,
  ExportListData,
  ApproveCreateData,
} from '@contracts'
import type { SortingDetails } from 'components/table/table-types'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils'
import type { FEApiError } from 'utils/errorHandling'
import type { ExtractElementType, ImportsCreateDataV5 } from 'utils/utils.d'
import styles from './RenderSelectedProjectRegion.module.scss'
import usePermissions from 'hooks/usePermissions'

interface RenderSelectedProjectRegionProps {
  singleProjectData: ProjectsDetailResult
  activeRegionData: ExtractElementType<ProjectsDetailResult['regions']>
  activeRegionId: string | undefined
  setToastDetails: Dispatch<SetStateAction<ToastDetails | undefined>>
  refreshSingleProject?: () => Promise<void>
  setIsOneRegionLoading: Dispatch<SetStateAction<boolean>>
}

const { PRODUCTS_API_ITEM_LIMITS } = DEFAULT_API_ITEM_LIMITS
const PROJECT_REGION_SEARCH_PARAM = 'project_region_id'

export default function RenderSelectedProjectRegion({
  singleProjectData,
  activeRegionData,
  activeRegionId,
  setToastDetails,
  refreshSingleProject,
  setIsOneRegionLoading,
}: RenderSelectedProjectRegionProps) {
  const navigate = useNavigate()
  const [isInitialising, setIsInitialising] = useState(true)
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false)
  const [isDeleteRegionModalOpen, setIsDeleteRegionModalOpen] = useState(false)
  const [isEditRegionModalOpen, setIsEditRegionModalOpen] = useState(false)
  const [isUploadDataModalOpen, setIsUploadDataModalOpen] = useState(false)
  const [isUploadDataLoading, setIsUploadDataLoading] = useState(false)
  const [shouldAppend, setShouldAppend] = useState(false)
  const { userName } = useCurrentUser()
  const [isDownloadLoading, setIsDownloadLoading] = useState(false)
  const [isGenerateContentModalOpen, setIsGenerateContentModalOpen] = useState(false)
  const [isBulkEditContentModalOpen, setIsBulkEditContentModalOpen] = useState(false)
  const [productsTableReducerState, productsTableReducerDispatch] = useReducer(
    paginatedTableReducer,
    getPaginatedTableReducerEmptyState(),
  )
  const { configApi } = useContext(appContext)
  const { data: configData } = configApi
  const { _id } = singleProjectData
  const retailersData = useMemo(() => {
    return getActiveRegionRetailers(configData, activeRegionData)
  }, [configData?.retailers, activeRegionData?.retailers])

  const hasRegionBeenSubmitted = useMemo(
    () => !!activeRegionData?.submitted_at || activeRegionData?.status?.toLocaleLowerCase() !== 'draft',
    [(activeRegionData?.submitted_at, activeRegionData?.status)],
  )

  // create URLs every time activeRegionId changes
  const { singleRegionDetails, regionProducts, regionSubmitUrl } = useMemo(
    () => ({
      singleRegionDetails: createCsApiUrlFromEndpoint(`v2/projects/${_id}/regions/${activeRegionId}`),
      regionProducts: createCsApiUrlFromEndpoint(
        `products?project_id=${_id}&${PROJECT_REGION_SEARCH_PARAM}=${activeRegionId}&position=0&limit=${PRODUCTS_API_ITEM_LIMITS}`,
      ),
      regionSubmitUrl: createCsApiUrlFromEndpoint(`v2/projects/${_id}/regions/${activeRegionId}/submit`),
    }),
    [activeRegionId],
  )

  const { fetchData: submitRegionContent, isLoading: isSubmitRegionLoading } = useApi<Response>(regionSubmitUrl)
  const { fetchData: exportJobs } = useApi<string>(`jobs/export?project_region_ids=${activeRegionId}`, {}, 0, 'csv')
  const { fetchData: exportProductDataFetch, setUrl: setExportProductUrl } = useApi<ExportListData>(
    `products/export?project_region_id=${activeRegionId}`,
  )
  const {
    fetchData: fetchApprovePost,
    isLoading: isFetchApproveLoading,
    url: fetchApproveUrl,
  } = useApi<ApproveCreateData>(createCsApiUrlFromEndpoint('jobs/approve'))

  // upload product step 1 => get upload bucket details
  const { fetchData: postProductUpload, url: postProductUploadUrl } = useApi<UploadsCreateData>(
    createCsApiUrlFromEndpoint('products/uploads'),
  )

  const { fetchData: postProductImports, url: postProductImportsUrl } = useApi<ImportsCreateDataV5 | Response>(
    'products/imports',
  )
  // single region API details
  const {
    fetchData: fetchSingleRegion,
    isLoading: isSingleRegionLoading,
    url: singleRegionUrl,
    setUrl: setSingleRegionUrl,
  } = useApi<ProjectsRegionsDeleteData | ProjectsRegionsPartialUpdateData>(singleRegionDetails)
  // region product API details
  const {
    fetchData: getRegionProduct,
    data: getRegionProductData,
    isLoading: isGetRegionProductLoading,
    url: getRegionProductUrl,
    error: getRegionProductError,
    setUrl: setGetRegionProductUrl,
  } = useApi<ProductsListData>(regionProducts)

  const { clientLevelPermissions } = usePermissions({
    clientId: singleProjectData?.client_id,
    permissionSubset: [
      'PDP_APP_GET_PRODUCTS',
      'PDP_APP_POST_V2_PROJECTS_BY_REGIONS_BY_SUBMIT',
      'PDP_APP_POST_PRODUCTS_UPLOADS',
      'PDP_APP_PATCH_V2_PROJECTS_BY_REGIONS_BY',
      'PDP_APP_DELETE_V2_PROJECTS_BY_REGIONS_BY',
      'PDP_APP_GET_PRODUCTS_EXPORT',
    ],
  })

  const [
    canGetProducts,
    canPostSubmitProject,
    canUploadProducts,
    canPatchProductRegion,
    canDeleteProductRegion,
    canGetExportProduct,
  ] = clientLevelPermissions

  // update URLs
  useEffect(() => {
    if (!activeRegionId) return
    setSingleRegionUrl(singleRegionDetails)
    setExportProductUrl(createCsApiUrlFromEndpoint(`products/export?project_region_id=${activeRegionId}`))

    // compare active region with the region in the regionProductUrl if different reset the setRegionProductUrl
    const getRegionProductQueryParameter = getRegionProductUrl.searchParams.get(PROJECT_REGION_SEARCH_PARAM)
    if (activeRegionId === getRegionProductQueryParameter) return
    setGetRegionProductUrl(regionProducts)
  }, [activeRegionId])

  // call region product API
  useEffect(() => {
    getRegionProduct(getRegionProductUrl)
      .then(() => {
        if (isInitialising) setIsInitialising(false)
      })
      .catch((error: FEApiError) => {
        setToastDetails({
          type: 'error',
          message: error.message || error.errors?.[0].message || 'An error occurred',
        })
      })
  }, [getRegionProductUrl])

  // update table reducer state
  useEffect(() => {
    setIsOneRegionLoading(isGetRegionProductLoading)
    if (isGetRegionProductLoading || !getRegionProductData || getRegionProductError) return

    productsTableReducerDispatch(
      setRowData({ rowData: getRegionProductData.products || [], shouldAppend: shouldAppend }),
    )
    productsTableReducerDispatch(
      setTaskPosition(shouldAppend ? getRegionProductData.position! + getRegionProductData.item_count! : 0),
    )
    productsTableReducerDispatch(setTotalItemCount(getRegionProductData.total_item_count!))
    setShouldAppend(false)
  }, [isGetRegionProductLoading])

  // ============= HANDLERS ============= //
  const handleDeleteRegion = async () => {
    if (!activeRegionId) throw new Error('Something went wrong, please try again later')
    try {
      await fetchSingleRegion(singleRegionUrl, {
        method: 'DELETE',
      })
      if (refreshSingleProject) refreshSingleProject()
      setToastDetails({
        message: 'Region deleted successfully',
        type: 'success',
      })
      setIsDeleteRegionModalOpen(false)
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }
  const handleEditRegion = async (data: ProjectsRegionsCreatePayload) => {
    if (
      data.retailers === activeRegionData?.retailers &&
      data.standard_retailer === activeRegionData?.standard_retailer
    ) {
      throw new Error('No changes detected')
    }

    try {
      await fetchSingleRegion(singleRegionUrl, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
      if (refreshSingleProject) refreshSingleProject()
      setIsEditRegionModalOpen(false)
      setToastDetails({
        message: 'Region updated successfully',
        type: 'success',
      })
    } catch (err) {
      const error = err as FEApiError
      if (error.status === 404 && refreshSingleProject) {
        await refreshSingleProject()
        setIsEditRegionModalOpen(false)
      }
      throw err
    }
  }
  const handleDownloadContentPrevGenerated = async () => {
    try {
      setIsDownloadLoading(true)
      const response = await exportProductDataFetch()
      const { download_url } = response

      if (!download_url) return
      const a = document.createElement('a')
      a.href = download_url
      a.download = `products-${activeRegionId}`
      a.click()
      a.remove()
    } catch (err) {
      console.error(err)
    } finally {
      setIsDownloadLoading(false)
    }
  }
  const handleDownloadContentGenerated = () => {
    setIsDownloadLoading(true)
    exportJobs()
      .then(exportedJobData => {
        downloadCSVFile(exportedJobData, 'region_tasks_output.csv')
      })
      .catch(console.error)
      .finally(() => setIsDownloadLoading(false))
  }

  const handleApproveAll = async () => {
    if (!activeRegionId || !_id) throw new Error('Something went wrong, please try again')
    try {
      await fetchApprovePost(fetchApproveUrl, {
        method: 'POST',
        body: JSON.stringify({
          project_id: _id,
          project_region_id: activeRegionId,
          update_info: {
            user_name: userName,
          },
        }),
      })

      if (refreshSingleProject) await refreshSingleProject()
      setToastDetails({
        message: 'Tasks approved successfully',
        type: 'success',
      })
      setIsApproveModalOpen(false)
      refreshTableHandler()
    } catch (err) {
      const error = err as FEApiError
      const errMessage =
        error.errors?.[0].message ||
        error.message ||
        (error.errors?.[0] as string | undefined) ||
        'Something went wrong'
      throw new Error(errMessage)
    }
  }
  const handleUploadContent = async (file: FileItemType, importProfile: string) => {
    try {
      setIsUploadDataLoading(true)
      // get bucket details
      const getUploadDetailsResponse = await postProductUpload(postProductUploadUrl, {
        method: 'POST',
        body: JSON.stringify({
          project_id: _id,
          file_name: file.name,
        }),
      })

      if (!getUploadDetailsResponse || !getUploadDetailsResponse.form_data || !getUploadDetailsResponse.url)
        throw new Error('Something went wrong, please try again later')

      const { form_data, url } = getUploadDetailsResponse
      const formDataUploadKey = form_data.key as string

      const uploadFileResponse = await uploadFileToBucket(form_data, file as File, url)
      if (!uploadFileResponse.ok) throw new Error('Failed to upload file')

      const response = await postProductImports(postProductImportsUrl, {
        method: 'POST',
        body: JSON.stringify(
          getType<ImportsCreatePayload>({
            project_id: _id,
            project_region_id: activeRegionId || undefined,
            upload_key: formDataUploadKey,
            file_name: file.name,
            import_profile: `pdpg-${importProfile}-template`,
          }),
        ),
      })

      if (response instanceof Response && !response.ok) throw new Error('Something went wrong, please try again later')

      setToastDetails({
        message: (
          <>
            Data is importing{' '}
            <b>
              <u>click to view log</u>
            </b>
          </>
        ),
        type: 'success',
        onBannerClick: () => navigate('logs'),
      })
      setIsUploadDataModalOpen(false)
    } catch (err) {
      throw err
    } finally {
      setIsUploadDataLoading(false)
    }
  }

  const menuItems: MenuItems = useMemo(() => {
    const returnMenuItems: MenuItems = []
    if (activeRegionData?.status === 'draft') {
      if (canUploadProducts)
        returnMenuItems.push({ label: 'Upload data', onClick: () => setIsUploadDataModalOpen(true) })
      if (canPatchProductRegion)
        returnMenuItems.push({ label: 'Edit region', onClick: () => setIsEditRegionModalOpen(true) })
      if (canDeleteProductRegion)
        returnMenuItems.push({ label: 'Delete region', onClick: () => setIsDeleteRegionModalOpen(true) })
    } else {
      if (canUploadProducts)
        returnMenuItems.push({
          label: 'Upload content',
          onClick: () => setIsBulkEditContentModalOpen(true),
        })
    }

    return returnMenuItems
  }, [canUploadProducts, canPatchProductRegion, canDeleteProductRegion, activeRegionData?.status])

  const refreshTableHandler = () => {
    refreshHandler(setShouldAppend, productsTableReducerDispatch, getRegionProductUrl, setGetRegionProductUrl)
  }
  const handleTableLoadMore = () => {
    handleLoadMore(
      getRegionProductData,
      setShouldAppend,
      PRODUCTS_API_ITEM_LIMITS,
      productsTableReducerDispatch,
      getRegionProductUrl,
      setGetRegionProductUrl,
    )
  }
  const handleTableSorting = (sortData: SortingDetails | undefined) => {
    handleSortingUpdate(
      sortData,
      setShouldAppend,
      getRegionProductUrl,
      productsTableReducerDispatch,
      setGetRegionProductUrl,
    )
  }
  const handleGenerateContent = async () => {
    try {
      await submitRegionContent(regionSubmitUrl, {
        method: 'POST',
      })
      setToastDetails({
        message: (
          <>
            Content has successfully started generating for{' '}
            <b>
              {activeRegionData?.region_name} ({activeRegionData?.region_locale?.toUpperCase().replace('_', '-')})
            </b>
          </>
        ),
        type: 'success',
      })
      setIsGenerateContentModalOpen(false)
      if (refreshSingleProject) await refreshSingleProject()
    } catch (err) {
      const error = err as FEApiError
      if (error.status === 422) {
        throw error
      }
      setToastDetails({
        type: 'error',
        message: error.errors?.[0].message || error.message || 'An error occurred',
      })
    }
  }

  return (
    <>
      <div className={styles.actionsContainer}>
        <div className={styles.retailersInner}>
          <WppTypography tag="p" type="xs-strong">
            Retailers:
          </WppTypography>
          {!retailersData || retailersData.length === 0 ? (
            <WppTypography tag="p" type="s-body">
              No retailers selected
            </WppTypography>
          ) : (
            <div>
              {retailersData.map(retailer => (
                <WppPill key={uuidv4()}>{retailer?.name}</WppPill>
              ))}
            </div>
          )}
        </div>
        <div className={styles.actions}>
          <MenuListPopUp menuItems={menuItems} />

          {canPostSubmitProject && !hasRegionBeenSubmitted && (
            <WppButton onClick={() => setIsGenerateContentModalOpen(true)}>Generate content</WppButton>
          )}
        </div>
      </div>

      <TableMetaDataRow
        text={`Showing ${productsTableReducerState.itemCount} results`}
        refreshHandler={refreshTableHandler}
        isInitialising={isGetRegionProductLoading || isInitialising}
        permissions={{ canRefresh: !isInitialising && canGetProducts }}
      >
        {canGetExportProduct && (
          <DownloadAllButton
            onClick={!hasRegionBeenSubmitted ? handleDownloadContentPrevGenerated : handleDownloadContentGenerated}
            isLoaded={isDownloadLoading}
            disabled={isGetRegionProductLoading || isInitialising}
          />
        )}

        {hasRegionBeenSubmitted && (
          <WppActionButton
            data-testid="approve-all-button"
            disabled={isGetRegionProductLoading || isInitialising}
            onClick={() => setIsApproveModalOpen(true)}
          >
            <WppIconDone slot="icon-start" />
            Approve all
          </WppActionButton>
        )}
      </TableMetaDataRow>

      <Table
        theme="tertiary"
        columnDefs={
          hasRegionBeenSubmitted ? columnStructureGeneratedRegion(retailersData.length) : columnStructureDraftRegion
        }
        loadedRows={productsTableReducerState.itemCount}
        totalRows={productsTableReducerState.totalItemCount}
        rowData={productsTableReducerState.rowData}
        isInitialising={isInitialising}
        loading={isGetRegionProductLoading}
        onLoadMore={handleTableLoadMore}
        onSortingUpdate={handleTableSorting}
        isDrillable
        onRowClicked={({ data }) => {
          navigate(`product/${data._id}`)
        }}
        noRowsOverlayComponent={() => (
          <WppTypography tag="p" type="s-body">
            {canGetProducts ? "You haven't uploaded any data yet " : 'You do not have permission to view this data '}
            <span>
              <WppActionButton onClick={() => setIsUploadDataModalOpen(true)} className={styles.upload}>
                click to upload
              </WppActionButton>
            </span>
          </WppTypography>
        )}
      />

      {isDeleteRegionModalOpen && (
        <GenericModal
          onCancel={() => setIsDeleteRegionModalOpen(false)}
          onSubmit={handleDeleteRegion}
          modalTitle="Delete region"
          submitButtonText="Delete"
          shouldModalOpen={isDeleteRegionModalOpen}
          isApiLoading={isSingleRegionLoading}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            This action will remove the region and anything associated with it from this project.
          </WppTypography>
        </GenericModal>
      )}

      {isEditRegionModalOpen && (
        <AddAndEditProjectRegion
          isModalOpen={isEditRegionModalOpen}
          isApiLoading={isSingleRegionLoading}
          onCancel={() => setIsEditRegionModalOpen(false)}
          onSubmit={handleEditRegion}
          regionData={activeRegionData}
        />
      )}

      {isUploadDataModalOpen && (
        <UploadProjectData
          onSubmit={handleUploadContent}
          onCancel={() => setIsUploadDataModalOpen(false)}
          modalTitle="Upload data"
          submitButtonText="Upload"
          isApiLoading={isUploadDataLoading}
          shouldModalOpen={isUploadDataModalOpen}
          selectedRegion={activeRegionData}
        />
      )}

      {isGenerateContentModalOpen && (
        <GenerateContentModal
          isOpen={isGenerateContentModalOpen}
          onClose={() => setIsGenerateContentModalOpen(false)}
          onSubmit={handleGenerateContent}
          selectedRegion={activeRegionData!}
          isLoading={isSubmitRegionLoading}
        />
      )}

      {isApproveModalOpen && (
        <GenericModal
          shouldModalOpen={isApproveModalOpen}
          modalTitle="Approve tasks"
          submitButtonText="Approve"
          isApiLoading={isFetchApproveLoading}
          onCancel={() => setIsApproveModalOpen(false)}
          onSubmit={handleApproveAll}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to approve <b>all tasks?</b>
            <br />
            This will move these into “completed” status.
          </WppTypography>
        </GenericModal>
      )}

      {isBulkEditContentModalOpen && (
        <ManualUpload
          shouldModalOpen={isBulkEditContentModalOpen}
          onCancel={() => setIsBulkEditContentModalOpen(false)}
          onSuccessCb={() => {
            setToastDetails({
              message: 'Content uploaded successfully',
              type: 'success',
            })
            setIsBulkEditContentModalOpen(false)
          }}
        />
      )}
    </>
  )
}
